import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useCountry } from '@/contexts/CountryContext';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  User, LogOut, Heart, FileText, Settings, ChevronDown,
  Home as HomeIcon, Gem,
  Calendar, Menu, X, Phone, Mail, ArrowRight, Globe, Compass, Ship, Building
} from 'lucide-react';



interface NavItem {
  label: string;
  path?: string;
  scrollTo?: string;
  children?: {
    label: string;
    path: string;
    desc: string;
    icon: React.FC<{ className?: string }>;
    image?: string;
  }[];
}

const albaniaNavItems: NavItem[] = [
  { label: 'Home', path: '/' },
  {
    label: 'Destinations',
    children: [
      { label: 'Albanian Riviera', path: '/albanian-travel', desc: 'Pristine beaches, turquoise waters & coastal villages', icon: Compass, image: 'https://images.unsplash.com/photo-1596394723269-e8e5a2e0c1f5?w=400&h=300&fit=crop' },
      { label: 'Luxury Travel', path: '/luxury-travel', desc: 'Premium curated experiences & 5-star stays', icon: Gem, image: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=400&h=300&fit=crop' },
      { label: 'Corporate Travel', path: '/corporate-travel', desc: 'Business retreats, events & team building', icon: Building, image: 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=400&h=300&fit=crop' },
    ],
  },
  {
    label: 'Experiences',
    children: [
      { label: 'Yacht Charters', path: '/yacht-charters', desc: 'Sail the Albanian Riviera in style', icon: Ship, image: 'https://images.unsplash.com/photo-1567899378494-47b22a2ae96a?w=400&h=300&fit=crop' },
      { label: 'Villa Rentals', path: '/villa-rentals', desc: 'Exclusive private villas & retreats', icon: HomeIcon, image: 'https://images.unsplash.com/photo-1613490493576-7fde63acd811?w=400&h=300&fit=crop' },
    ],
  },
  { label: 'Journal', path: '/blog' },

];

// Romania site keeps everything inside its own /romania/* namespace so no
// Albanian links ever appear on the Romanian side.
const romaniaNavItems: NavItem[] = [
  { label: 'Home', path: '/romania' },
  { label: 'Destinations', path: '/romania/destinations' },
  { label: 'Experiences', path: '/romania/experiences' },
  { label: 'Journal', path: '/romania/journal' },

];


const Navbar: React.FC = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [mobileExpanded, setMobileExpanded] = useState<string | null>(null);
  const dropdownTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const navRef = useRef<HTMLDivElement>(null);
  const { user, profile, signOut, setShowAuthModal, savedDestinations } = useAuth();
  const { country } = useCountry();
  const navItems = country === 'romania' ? romaniaNavItems : albaniaNavItems;
  const navigate = useNavigate();
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) setUserMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
    setActiveDropdown(null);
  }, [location.pathname]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileMenuOpen]);

  const handleDropdownEnter = (label: string) => {
    if (dropdownTimeoutRef.current) clearTimeout(dropdownTimeoutRef.current);
    setActiveDropdown(label);
  };

  const handleDropdownLeave = () => {
    dropdownTimeoutRef.current = setTimeout(() => setActiveDropdown(null), 200);
  };

  const scrollToSection = (id: string) => {
    if (!isHomePage) {
      navigate('/');
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 300);
    } else {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }
    setMobileMenuOpen(false);
  };

  const handleLogoClick = () => {
    if (!isHomePage) navigate('/');
    else window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSignOut = async () => {
    await signOut();
    setUserMenuOpen(false);
    if (!isHomePage) navigate('/');
  };

  const getInitials = () => {
    if (profile?.first_name || profile?.last_name) {
      return `${(profile.first_name || '')[0] || ''}${(profile.last_name || '')[0] || ''}`.toUpperCase();
    }
    return user?.email?.[0]?.toUpperCase() || 'U';
  };

  const isActive = (path?: string) => {
    if (!path) return false;
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <>
      {/* Top utility bar */}
      <div className={`fixed top-0 left-0 right-0 z-[60] transition-all duration-500 ${scrolled ? 'h-0 opacity-0 overflow-hidden' : 'h-8 opacity-100'}`}>
        <div className="h-full bg-[#0D1B2A]/60 backdrop-blur-sm border-b border-white/5">
          <div className="max-w-[1440px] mx-auto px-6 lg:px-12 h-full flex items-center justify-between">
            <div className="flex items-center gap-6">
              <a href="mailto:journey@zeratravel.com" className="flex items-center gap-1.5 text-white/50 hover:text-[#C9A961] transition-colors">
                <Mail className="w-3 h-3" />
                <span className="text-[10px] tracking-wider font-sans">journey@zeratravel.com</span>
              </a>

              <a href="tel:+15612472536" className="hidden sm:flex items-center gap-1.5 text-white/50 hover:text-[#C9A961] transition-colors">
                <Phone className="w-3 h-3" />
                <span className="text-[10px] tracking-wider font-sans">+1 561-247-2536</span>
              </a>
            </div>


            <div className="flex items-center gap-4">
              <button className="flex items-center gap-1 text-white/50 hover:text-[#C9A961] transition-colors">
                <Globe className="w-3 h-3" />
                <span className="text-[10px] tracking-wider font-sans">EN</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main navbar */}
      <nav
        className={`fixed left-0 right-0 z-50 transition-all duration-500 ${
          scrolled
            ? 'top-0 bg-[#0D1B2A]/95 backdrop-blur-xl shadow-2xl shadow-black/20 border-b border-white/5'
            : 'top-8 bg-gradient-to-b from-black/40 via-black/20 to-transparent'
        }`}
      >
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className={`flex items-center justify-between transition-all duration-500 ${scrolled ? 'h-16 lg:h-[72px]' : 'h-20 lg:h-24'}`}>


            {/* Logo */}
            <button onClick={handleLogoClick} className="flex items-center gap-2.5 group flex-shrink-0">
              <span className="font-serif text-2xl lg:text-[28px] tracking-[0.35em] font-light text-white group-hover:text-[#C9A961] transition-colors duration-500">
                ZERA
              </span>
              <div className="flex flex-col items-start border-l border-white/20 pl-2.5">

                <span className="font-sans text-[10px] tracking-[0.25em] uppercase text-[#C9A961] leading-none">Travel</span>
                <span className="font-sans text-[8px] tracking-[0.2em] uppercase leading-none mt-0.5 text-white/40">{country === 'romania' ? 'Romania' : 'Albania'}</span>

              </div>
            </button>

            {/* Desktop Navigation - Center */}
            <div className="hidden lg:flex items-center" ref={navRef}>
              <ul className="flex items-center gap-0">
                {navItems.map((item) => (
                  <li
                    key={item.label}
                    className="relative"
                    onMouseEnter={() => item.children ? handleDropdownEnter(item.label) : undefined}
                    onMouseLeave={() => item.children ? handleDropdownLeave() : undefined}
                  >
                    <button
                      onClick={() => {
                        if (item.path) {
                          navigate(item.path);
                          setActiveDropdown(null);
                        } else if (item.scrollTo) {
                          scrollToSection(item.scrollTo);
                          setActiveDropdown(null);
                        } else if (item.children) {
                          setActiveDropdown(activeDropdown === item.label ? null : item.label);
                        }
                      }}
                      className={`
                        relative px-5 py-2 font-sans text-[12px] tracking-[0.18em] uppercase transition-all duration-300
                        flex items-center gap-1.5 group
                        ${isActive(item.path) ? 'text-[#C9A961]' : 'text-white/80 hover:text-white'}
                      `}
                    >
                      <span className="relative">
                        {item.label}
                        {/* Active indicator line */}
                        <span className={`absolute -bottom-1 left-0 h-[1px] bg-[#C9A961] transition-all duration-300 ${isActive(item.path) ? 'w-full' : 'w-0 group-hover:w-full'}`} />
                      </span>
                      {item.children && (
                        <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-300 ${activeDropdown === item.label ? 'rotate-180 text-[#C9A961]' : ''}`} />
                      )}
                    </button>

                    {/* Mega Dropdown */}
                    {item.children && activeDropdown === item.label && (
                      <div
                        className="absolute top-full left-1/2 -translate-x-1/2 pt-4"
                        onMouseEnter={() => handleDropdownEnter(item.label)}
                        onMouseLeave={handleDropdownLeave}
                      >
                        <div className="bg-[#0D1B2A] border border-white/10 shadow-2xl shadow-black/40 min-w-[420px] animate-in fade-in slide-in-from-top-2 duration-200">
                          {/* Gold accent line at top */}
                          <div className="h-[2px] bg-gradient-to-r from-transparent via-[#C9A961] to-transparent" />

                          <div className="p-3">
                            {item.children.map((child, idx) => (
                              <button
                                key={child.path}
                                onClick={() => { navigate(child.path); setActiveDropdown(null); }}
                                className="w-full flex items-center gap-4 p-3 rounded-lg hover:bg-white/5 transition-all duration-200 group/item"
                              >
                                {/* Icon */}
                                <div className="w-11 h-11 rounded-lg bg-[#C9A961]/10 border border-[#C9A961]/20 flex items-center justify-center flex-shrink-0 group-hover/item:bg-[#C9A961]/20 group-hover/item:border-[#C9A961]/40 transition-all">
                                  {React.createElement(child.icon, { className: 'w-5 h-5 text-[#C9A961]' })}
                                </div>

                                {/* Text */}
                                <div className="flex-1 text-left">
                                  <span className="block font-sans text-[13px] tracking-wider text-white group-hover/item:text-[#C9A961] transition-colors font-medium">
                                    {child.label}
                                  </span>
                                  <span className="block font-sans text-[11px] text-white/40 mt-0.5 leading-relaxed">
                                    {child.desc}
                                  </span>
                                </div>

                                {/* Arrow */}
                                <ArrowRight className="w-4 h-4 text-white/20 group-hover/item:text-[#C9A961] group-hover/item:translate-x-1 transition-all flex-shrink-0" />
                              </button>
                            ))}
                          </div>

                          {/* Bottom CTA */}
                          <div className="border-t border-white/5 px-6 py-3">
                            <button
                              onClick={() => { navigate('/booking'); setActiveDropdown(null); }}
                              className="flex items-center gap-2 text-[#C9A961]/70 hover:text-[#C9A961] transition-colors group/cta"
                            >
                              <Calendar className="w-3.5 h-3.5" />
                              <span className="font-sans text-[10px] tracking-[0.15em] uppercase">Plan your journey</span>
                              <ArrowRight className="w-3 h-3 group-hover/cta:translate-x-1 transition-transform" />
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </div>

            {/* Right side actions */}
            <div className="flex items-center gap-2 lg:gap-3 flex-shrink-0">
              {/* User menu */}
              {user ? (
                <div className="relative" ref={userMenuRef}>
                  <button
                    onClick={() => setUserMenuOpen(!userMenuOpen)}
                    className="flex items-center gap-2 group px-2 py-1.5 rounded-full hover:bg-white/10 transition-all"
                  >
                    <div className="w-8 h-8 rounded-full flex items-center justify-center font-sans text-[11px] font-medium bg-[#C9A961]/20 text-[#C9A961] border border-[#C9A961]/30">
                      {getInitials()}
                    </div>
                    <ChevronDown className={`w-3 h-3 text-white/50 transition-transform hidden md:block ${userMenuOpen ? 'rotate-180' : ''}`} />
                  </button>

                  {userMenuOpen && (
                    <div className="absolute right-0 top-full mt-3 w-72 bg-[#0D1B2A] border border-white/10 shadow-2xl shadow-black/40 animate-in fade-in slide-in-from-top-2 duration-200 z-50">
                      <div className="h-[2px] bg-gradient-to-r from-transparent via-[#C9A961] to-transparent" />
                      <div className="px-5 py-4 border-b border-white/5">
                        <p className="font-serif text-base text-white">{profile?.first_name || ''} {profile?.last_name || ''}</p>
                        <p className="font-sans text-[10px] text-white/40 truncate mt-0.5">{user.email}</p>
                      </div>
                      <div className="py-2">
                        {[
                          { icon: User, label: 'My Profile', path: '/profile' },
                          { icon: Heart, label: 'Saved Destinations', path: '/profile?tab=saved', badge: savedDestinations.length },
                          { icon: FileText, label: 'Journey Requests', path: '/profile?tab=journeys' },
                          { icon: Settings, label: 'Preferences', path: '/profile?tab=preferences' },
                        ].map((item) => (
                          <button
                            key={item.label}
                            onClick={() => { navigate(item.path); setUserMenuOpen(false); }}
                            className="w-full flex items-center gap-3 px-5 py-3 font-sans text-[11px] tracking-wider text-white/60 hover:bg-white/5 hover:text-white transition-colors"
                          >
                            <item.icon className="w-4 h-4 text-[#C9A961]" />
                            {item.label}
                            {item.badge && item.badge > 0 && (
                              <span className="ml-auto bg-[#C9A961] text-[#0D1B2A] text-[9px] w-5 h-5 rounded-full flex items-center justify-center font-bold">{item.badge}</span>
                            )}
                          </button>
                        ))}
                      </div>
                      <div className="border-t border-white/5 py-2">
                        <button onClick={handleSignOut} className="w-full flex items-center gap-3 px-5 py-3 font-sans text-[11px] tracking-wider text-white/40 hover:bg-red-500/10 hover:text-red-400 transition-colors">
                          <LogOut className="w-4 h-4" /> Sign Out
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <button
                  onClick={() => setShowAuthModal(true)}
                  className="flex items-center gap-2 px-3 py-2 rounded-full hover:bg-white/10 transition-all text-white/80 hover:text-white"
                >
                  <User className="w-4 h-4" />
                  <span className="hidden md:inline font-sans text-[11px] tracking-[0.12em] uppercase">Sign In</span>
                </button>
              )}

              {/* Reserve CTA */}
              <button
                onClick={() => navigate('/booking')}
                className="hidden md:flex items-center gap-2 font-sans text-[10px] tracking-[0.2em] uppercase px-5 py-2.5 bg-[#C9A961] text-[#0D1B2A] hover:bg-[#d4b46e] transition-all duration-300 font-semibold"
              >
                <Calendar className="w-3.5 h-3.5" />
                Reserve
              </button>

              {/* Mobile menu toggle */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="lg:hidden flex items-center justify-center w-10 h-10 rounded-full hover:bg-white/10 transition-all"
                aria-label="Toggle menu"
              >
                {mobileMenuOpen ? (
                  <X className="w-5 h-5 text-white" />
                ) : (
                  <Menu className="w-5 h-5 text-white" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Subtle bottom border when not scrolled */}
        {!scrolled && <div className="h-[1px] bg-gradient-to-r from-transparent via-white/10 to-transparent" />}
      </nav>

      {/* Mobile Menu Overlay */}
      <div
        className={`fixed inset-0 z-[45] transition-all duration-500 lg:hidden ${
          mobileMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
      >
        {/* Backdrop */}
        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setMobileMenuOpen(false)} />

        {/* Menu Panel */}
        <div className={`absolute top-0 right-0 h-full w-full max-w-[380px] bg-[#0D1B2A] shadow-2xl transition-transform duration-500 ${mobileMenuOpen ? 'translate-x-0' : 'translate-x-full'}`}>
          {/* Mobile menu header */}
          <div className="flex items-center justify-between px-6 h-20 border-b border-white/5">
            <span className="font-serif text-xl tracking-[0.3em] text-white">ZERA</span>
            <button onClick={() => setMobileMenuOpen(false)} className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-white/10 transition-colors">
              <X className="w-5 h-5 text-white/70" />
            </button>
          </div>

          {/* Mobile menu content */}
          <div className="overflow-y-auto h-[calc(100%-80px)] pb-20">
            <div className="py-4">
              {navItems.map((item) => (
                <div key={item.label}>
                  {item.children ? (
                    <>
                      <button
                        onClick={() => setMobileExpanded(mobileExpanded === item.label ? null : item.label)}
                        className="w-full flex items-center justify-between px-6 py-4 text-white/80 hover:text-white hover:bg-white/5 transition-all"
                      >
                        <span className="font-sans text-[13px] tracking-[0.18em] uppercase">{item.label}</span>
                        <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${mobileExpanded === item.label ? 'rotate-180 text-[#C9A961]' : ''}`} />
                      </button>

                      <div className={`overflow-hidden transition-all duration-300 ${mobileExpanded === item.label ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                        <div className="bg-white/[0.02] border-t border-b border-white/5">
                          {item.children.map((child) => (
                            <button
                              key={child.path}
                              onClick={() => { navigate(child.path); setMobileMenuOpen(false); }}
                              className="w-full flex items-center gap-4 px-8 py-4 hover:bg-white/5 transition-all group"
                            >
                              <div className="w-9 h-9 rounded-lg bg-[#C9A961]/10 border border-[#C9A961]/20 flex items-center justify-center flex-shrink-0">
                                {React.createElement(child.icon, { className: 'w-4 h-4 text-[#C9A961]' })}
                              </div>
                              <div className="text-left">
                                <span className="block font-sans text-[12px] tracking-wider text-white/80 group-hover:text-[#C9A961] transition-colors">
                                  {child.label}
                                </span>
                                <span className="block font-sans text-[10px] text-white/30 mt-0.5">
                                  {child.desc}
                                </span>
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>
                    </>
                  ) : (
                    <button
                      onClick={() => {
                        if (item.path) navigate(item.path);
                        else if (item.scrollTo) scrollToSection(item.scrollTo);
                        setMobileMenuOpen(false);
                      }}
                      className={`w-full flex items-center px-6 py-4 font-sans text-[13px] tracking-[0.18em] uppercase transition-all hover:bg-white/5 ${
                        isActive(item.path) ? 'text-[#C9A961]' : 'text-white/80 hover:text-white'
                      }`}
                    >
                      {item.label}
                    </button>
                  )}
                </div>
              ))}
            </div>

            {/* Mobile CTA section */}
            <div className="px-6 pt-4 border-t border-white/5 space-y-3">
              <button
                onClick={() => { navigate('/booking'); setMobileMenuOpen(false); }}
                className="w-full flex items-center justify-center gap-2 font-sans text-[11px] tracking-[0.2em] uppercase px-6 py-4 bg-[#C9A961] text-[#0D1B2A] hover:bg-[#d4b46e] transition-all font-semibold"
              >
                <Calendar className="w-4 h-4" />
                Reserve Your Journey
              </button>

              {user ? (
                <div className="space-y-1 pt-2">
                  <button
                    onClick={() => { navigate('/profile'); setMobileMenuOpen(false); }}
                    className="w-full flex items-center gap-3 px-2 py-3 font-sans text-[12px] tracking-wider text-white/60 hover:text-white transition-colors"
                  >
                    <User className="w-4 h-4 text-[#C9A961]" />
                    My Profile
                  </button>
                  <button
                    onClick={() => { handleSignOut(); setMobileMenuOpen(false); }}
                    className="w-full flex items-center gap-3 px-2 py-3 font-sans text-[12px] tracking-wider text-white/30 hover:text-red-400 transition-colors"
                  >
                    <LogOut className="w-4 h-4" />
                    Sign Out
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => { setShowAuthModal(true); setMobileMenuOpen(false); }}
                  className="w-full flex items-center justify-center gap-2 font-sans text-[11px] tracking-[0.2em] uppercase px-6 py-4 border border-white/20 text-white hover:bg-white/5 transition-all"
                >
                  <User className="w-4 h-4" />
                  Sign In / Create Account
                </button>
              )}
            </div>

            {/* Mobile contact info */}
            {/* Mobile contact info */}
            <div className="px-6 pt-8 pb-6 space-y-3">
              <a href="mailto:journey@zeratravel.com" className="flex items-center gap-3 text-white/30 hover:text-[#C9A961] transition-colors">
                <Mail className="w-4 h-4" />
                <span className="font-sans text-[11px] tracking-wider">journey@zeratravel.com</span>
              </a>

              <a href="tel:+15612472536" className="flex items-center gap-3 text-white/30 hover:text-[#C9A961] transition-colors">
                <Phone className="w-4 h-4" />
                <span className="font-sans text-[11px] tracking-wider">+1 561-247-2536</span>
              </a>
            </div>


          </div>
        </div>
      </div>
    </>
  );
};

export default Navbar;
